import { ApolloQueryResult } from '@apollo/client'
import {
  Box,
  Icon,
  Button as OldButton,
  Spinner,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { ProductUpdatesSubscriptionsQuery } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useCreateProductUpdatesSubscriptions } from 'modules/product_updates/hooks/useCreateProductUpdatesSubscriptions'
import { useDeleteProductUpdatesSubscriptions } from 'modules/product_updates/hooks/useDeleteProductUpdatesSubscriptions'
import { useGetProductUpdatesSubscriptions } from 'modules/product_updates/hooks/useGetProductUpdatesSubscriptions'

type ButtonProps = {
  productUpdateId: string
  refetchSubscription: () => Promise<ApolloQueryResult<ProductUpdatesSubscriptionsQuery>>
  productUpdateSlug: string
  version?: 'factorial-one'
  source?: string
}

const SubscribeButton = ({
  productUpdateId,
  refetchSubscription,
  productUpdateSlug,
  version,
  source,
}: ButtonProps) => {
  const [open, toggle] = useToggle(false)
  const { createSubscription } = useCreateProductUpdatesSubscriptions()

  const onClick = async () => {
    const result = await createSubscription({ variables: { productUpdateId, productUpdateSlug } })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      track(trackingEvents.productUpdates.subscribeToProductUpdate, {
        product_update_slug: productUpdateSlug,
        source,
      })
      await refetchSubscription()
    }
  }

  return (
    <>
      {version === 'factorial-one' ? (
        <Button
          variant="promote"
          onClick={onClick}
          label={i18n.t('product_updates.subscribe.get_updates')}
        />
      ) : (
        <>
          <OldButton type="primary" onClick={onClick}>
            {i18n.t('product_updates.subscribe.get_updates')}
          </OldButton>
          <Tooltip.Alt
            attachment="bottom center"
            open={open}
            toggle={toggle}
            label={i18n.t('product_updates.subscribe.tooltip_info')}
          >
            <Icon.Medium icon="info-circle-line" color="grey800" />
          </Tooltip.Alt>
        </>
      )}
    </>
  )
}

type ButtonsProps = {
  productUpdateId: string
  productUpdateSlug: string
  version?: 'factorial-one'
  source?: string
}

const SubscriptionButtons = ({
  productUpdateId,
  productUpdateSlug,
  version,
  source,
}: ButtonsProps) => {
  const { loading: unsubscribeLoading, deleteSubscription } = useDeleteProductUpdatesSubscriptions()
  const {
    loading: subscriptionsLoading,
    productUpdatesSubscriptions,
    refetch,
  } = useGetProductUpdatesSubscriptions()

  const isAlreadySubscribed = (productUpdatesSubscriptions || []).some(
    (subscription) => subscription.productUpdateId === productUpdateId
  )

  if (subscriptionsLoading || unsubscribeLoading) return <Spinner />

  const onClick = async () => {
    const result = await deleteSubscription({ variables: { productUpdateId } })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      track(trackingEvents.productUpdates.unsubscribeFromProductUpdate, {
        product_update_slug: productUpdateSlug,
        source,
      })
      await refetch()
    }
  }

  return (
    <Box flexDirection="row" alignItems="center" gap="s6">
      {isAlreadySubscribed ? (
        version === 'factorial-one' ? (
          <Button
            variant="neutral"
            onClick={onClick}
            label={i18n.t('product_updates.subscribe.unsubscribe')}
          />
        ) : (
          <OldButton type="secondary" onClick={onClick}>
            {i18n.t('product_updates.subscribe.unsubscribe')}
          </OldButton>
        )
      ) : (
        <SubscribeButton
          productUpdateId={productUpdateId}
          refetchSubscription={refetch}
          productUpdateSlug={productUpdateSlug}
          version={version}
        />
      )}
    </Box>
  )
}

export default SubscriptionButtons
